/**
 * @file BusinessAnalyticsPreview.tsx
 * @brief DEV ONLY: Super Admin → Analytics with B25 answered in the page
 *        (`/__preview/business-analytics`, W22 25-10-2026).
 *
 * @details
 * `b25-fixture.json` is the real B25 service (BACKEND 87602a1) run on its own
 * PGlite test seed (October 2026, Europe/London, FX 0.79 fallback), so every
 * shape and number is what the backend produces; nothing is hand-made. The
 * same payloads answer any period.
 *
 * `?state=` empty (a week with nothing in it) · nullmrr (a Stripe price that
 * can't be read) · churn (a window after 21-10-2026, churn recorded) ·
 * error (400 BAD_WINDOW on every view) · loading (never answers) ·
 * live (a live exchangerate.host quote instead of the fallback).
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { useEffect, useState } from "react";
import { ApiClientError } from "@/shared/api/client";
import { AnalyticsPage } from "@/features/analytics/AnalyticsPage";
import { __setBusinessAnalyticsStub } from "../api";
import type { BusinessView } from "../types";

type Fixture = Record<string, unknown> & { empty: Record<string, unknown>; saasAfter: Record<string, unknown> };

const STATE = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search).get("state");

function answer(fx: Fixture, view: BusinessView): unknown {
  const base = structuredClone((STATE === "empty" && fx.empty[view] ? fx.empty[view] : fx[view]) as Record<string, unknown>);
  if (STATE === "churn" && view === "saas") return structuredClone(fx.saasAfter);
  if (STATE === "nullmrr" && (view === "overview" || view === "saas")) {
    const reason = "1 paying subscription(s) have no readable Stripe price, so MRR would be a guess.";
    base.mrr_known_part = view === "saas" ? { gbp: 7138.67, usd: 9036.29, by_currency: { GBP: 7138.67 } } : undefined;
    Object.assign(base, { mrr: null, arr: null, mrr_reason: reason });
    if (view === "saas") {
      (base.arpu as Record<string, unknown>).arpa_mrr = null;
      (base.data_quality as Record<string, unknown>).mrr_unpriced = [
        { account_type: "personal", id: "sub-row-9", stripe_price_id: "price_pro_old", reason: "Stripe price not readable: resource_missing" },
      ];
    }
  }
  if (STATE === "live") base.fx = { usd_to_gbp: 0.7712, source: "exchangerate.host", fetched_at: "2026-10-25T09:00:00.000Z", note: "One rate for the whole response." };
  return base;
}

export function BusinessAnalyticsPreviewPage() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let fixture: Fixture | null = null;
    __setBusinessAnalyticsStub(async (view) => {
      if (STATE === "loading") return new Promise(() => undefined);
      if (STATE === "error") throw new ApiClientError("from must be before to", 400, "BAD_WINDOW");
      fixture ??= (await import("./b25-fixture.json")).default as unknown as Fixture;
      await new Promise((r) => setTimeout(r, 250));
      return answer(fixture, view);
    });
    setReady(true);
    return () => __setBusinessAnalyticsStub(null);
  }, []);
  return ready ? <AnalyticsPage /> : null;
}
